"use client";

import { cn } from "@newxsystems/shared";
import { Icon } from "@templates/design-system";
import { useEffect, useRef, useState } from "react";
import { getComponent } from "../../../../client";
import { StratosAction } from "../StratosAction/StratosAction";
import { StratosMedia } from "../StratosMedia/StratosMedia";
import type { IMegaMenuNavigationProps } from "./MegaMenuNavigation.props";

export const MegaMenuNavigation = ({ actions, items, logo, sticky }: IMegaMenuNavigationProps) => {
  const Link = getComponent("Link");
  const [openDesktop, setOpenDesktop] = useState<number | null>(null);
  const [openMobile, setOpenMobile] = useState<number | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const headerRef = useRef<HTMLElement>(null);
  const dialogRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const close = (event: MouseEvent) => {
      if (!headerRef.current?.contains(event.target as Node)) setOpenDesktop(null);
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpenDesktop(null);
    };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", escape);
    };
  }, []);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (drawerOpen && !dialog.open) dialog.showModal();
    if (!drawerOpen && dialog.open) dialog.close();
  }, [drawerOpen]);
  const navigate = () => {
    setDrawerOpen(false);
    setOpenMobile(null);
  };
  const linkClass =
    "inline-flex min-h-11 items-center px-3 font-stratos-body text-sm font-semibold text-foreground transition-colors hover:text-primary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring";
  const nestedLink = (
    link: {
      accessibilityLabel?: string | null;
      href: string;
      label: string;
      openInNewTab?: boolean | null;
    },
    key: number,
    mobile = false,
  ) => (
    <Link
      aria-label={link.accessibilityLabel || undefined}
      className={cn(linkClass, "w-full")}
      href={link.href}
      key={key}
      onClick={mobile ? navigate : undefined}
      rel={link.openInNewTab ? "noopener noreferrer" : undefined}
      target={link.openInNewTab ? "_blank" : undefined}
    >
      {link.label}
    </Link>
  );

  if (!logo && !items?.length && !actions?.length) return null;

  return (
    <header
      className={cn("z-40 border-b border-horizon-blue bg-cloud-white", sticky && "sticky top-0")}
      ref={headerRef}
    >
      <div className="stratos-container flex min-h-24 items-center gap-8">
        {logo ? (
          <Link
            aria-label="Home"
            className="shrink-0 rounded-full focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
            href="/"
          >
            <StratosMedia
              className="h-14 w-56 bg-transparent"
              imageClassName="object-contain object-left"
              media={logo}
              sizes="192px"
            />
          </Link>
        ) : null}
        <nav aria-label="Primary" className="ml-auto hidden items-center gap-1 lg:flex">
          {items?.map((item, index) =>
            item.blockType === "megaMenuDirectLink" ? (
              nestedLink(item.link, index)
            ) : (
              <div className="relative" key={index}>
                <button
                  aria-expanded={openDesktop === index}
                  className={cn(linkClass, "cursor-pointer gap-1")}
                  onClick={() => setOpenDesktop(openDesktop === index ? null : index)}
                  type="button"
                >
                  {item.label}
                  <Icon
                    className={cn(
                      "size-4 transition-transform",
                      openDesktop === index && "rotate-180",
                    )}
                    name="chevron-down"
                  />
                </button>
                {openDesktop === index ? (
                  <div
                    className={cn(
                      "absolute right-0 top-full z-50 mt-2 min-w-64 overflow-hidden rounded-[20px] border border-horizon-blue bg-cloud-white p-2 shadow-[0_18px_42px_-22px_rgb(16_23_34/0.45)]",
                      item.blockType === "megaMenuGroup" &&
                        "grid min-w-[36rem] grid-cols-2 gap-4 p-4",
                    )}
                  >
                    {item.blockType === "megaMenuDropdown"
                      ? item.links.map((link, linkIndex) => nestedLink(link, linkIndex))
                      : item.groupItems.map((group, groupIndex) => (
                          <div key={groupIndex}>
                            <p className="border-b border-horizon-blue px-3 py-2 font-stratos-body text-xs text-primary">
                              {group.header}
                            </p>
                            {group.links.map((link, linkIndex) => nestedLink(link, linkIndex))}
                          </div>
                        ))}
                  </div>
                ) : null}
              </div>
            ),
          )}
        </nav>
        {actions?.length ? (
          <div className="hidden gap-2 lg:flex">
            {actions.map((action, index) => (
              <StratosAction action={action} key={index} />
            ))}
          </div>
        ) : null}
        <button
          aria-label="Open navigation menu"
          className="ml-auto inline-flex size-11 items-center justify-center rounded-full border border-horizon-blue focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring lg:hidden"
          onClick={() => setDrawerOpen(true)}
          type="button"
        >
          <Icon name="menu" />
        </button>
      </div>
      <dialog
        className="m-0 ml-auto h-dvh max-h-none w-full max-w-none bg-transparent p-0 backdrop:bg-night-flight/45 md:w-[28rem]"
        onClose={() => setDrawerOpen(false)}
        ref={dialogRef}
      >
        <div className="flex h-full w-full flex-col bg-cloud-white p-6 shadow-[-20px_0_48px_-28px_rgb(16_23_34/0.5)]">
          <div className="flex items-center justify-between border-b border-horizon-blue pb-4">
            {logo ? (
              <Link
                aria-label="Home"
                className="rounded-full focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
                href="/"
                onClick={navigate}
              >
                <StratosMedia
                  className="h-10 w-44 bg-transparent"
                  imageClassName="object-contain object-left"
                  media={logo}
                  sizes="176px"
                />
              </Link>
            ) : (
              <span className="font-stratos-body text-xs">Menu</span>
            )}
            <button
              aria-label="Close navigation menu"
              className="inline-flex size-11 items-center justify-center rounded-full border border-horizon-blue focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
              onClick={() => setDrawerOpen(false)}
              type="button"
            >
              <Icon name="x" />
            </button>
          </div>
          <nav aria-label="Mobile primary" className="flex-1 overflow-y-auto py-4">
            {items?.map((item, index) =>
              item.blockType === "megaMenuDirectLink" ? (
                nestedLink(item.link, index, true)
              ) : (
                <div className="border-b border-horizon-blue" key={index}>
                  <button
                    aria-expanded={openMobile === index}
                    className={cn(linkClass, "w-full cursor-pointer justify-between")}
                    onClick={() => setOpenMobile(openMobile === index ? null : index)}
                    type="button"
                  >
                    {item.label}
                    <Icon
                      className={cn(
                        "size-4 transition-transform",
                        openMobile === index && "rotate-180",
                      )}
                      name="chevron-down"
                    />
                  </button>
                  {openMobile === index ? (
                    <div className="pb-3 pl-3">
                      {item.blockType === "megaMenuDropdown"
                        ? item.links.map((link, linkIndex) => nestedLink(link, linkIndex, true))
                        : item.groupItems.map((group, groupIndex) => (
                            <div key={groupIndex}>
                              <p className="px-3 py-2 font-stratos-body text-xs text-primary">
                                {group.header}
                              </p>
                              {group.links.map((link, linkIndex) =>
                                nestedLink(link, linkIndex, true),
                              )}
                            </div>
                          ))}
                    </div>
                  ) : null}
                </div>
              ),
            )}
          </nav>
          {actions?.length ? (
            <div className="flex flex-col gap-2 border-t border-horizon-blue pt-4">
              {actions.map((action, index) => (
                <StratosAction action={action} key={index} />
              ))}
            </div>
          ) : null}
        </div>
      </dialog>
    </header>
  );
};

MegaMenuNavigation.displayName = "Content::MegaMenuNavigation";
